/**
 * ExportReportModal - AI-curated PDF export for copilot threads.
 *
 * Flow:
 *   1. On open → calls curate-report endpoint → LLM suggests items
 *   2. User reviews: editable summary + item checkboxes
 *   3. User clicks "Generate PDF" → frontend collects raw ECharts specs,
 *      sends selected items + chart specs to export-pdf endpoint
 *   4. Backend renders charts in Playwright using ECharts CDN + reviveFunctions()
 *   5. Browser downloads the PDF
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useCopilotStore } from '../stores/useCopilotStore';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  CircularProgress,
  Alert,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import { AppDialog } from '../../../components/dialog';
import TextSnippetRounded from '@mui/icons-material/TextSnippetRounded';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import PushPinRounded from '@mui/icons-material/PushPinRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import { buildApiUrl, config, tenantApiPrefix } from '../../../config';
import { getTenantIdFromStorage } from '../../../utils/onboardingLocalStorage';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReportableItem {
  id: string;
  msg_index: number;
  type: 'text' | 'chart' | 'table' | 'finding';
  label: string;
  content_preview: string;
  include: boolean;
  artifact_id?: string;
  finding_data?: {
    title: string;
    narrative: string;
    data_point: Record<string, any>;
  };
}

interface ExportReportModalProps {
  open: boolean;
  onClose: () => void;
  threadId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strip common markdown syntax for plain-text display in the modal. */
function stripMarkdown(text: string): string {
  return text
    .replace(/#{1,6}\s*/g, '')          // headings
    .replace(/\*\*(.+?)\*\*/g, '$1')    // bold
    .replace(/\*(.+?)\*/g, '$1')        // italic
    .replace(/`{1,3}([^`]*)`{1,3}/g, '$1') // code / backticks
    .replace(/\[(.+?)\]\(.+?\)/g, '$1') // links
    .replace(/^\s*[-*+]\s+/gm, '')      // list bullets
    .replace(/^\s*\d+\.\s+/gm, '')      // numbered lists
    .replace(/\n{2,}/g, ' ')            // collapse blank lines
    .replace(/\n/g, ' ')                // newlines → space
    .trim();
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('klaris_jwt');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

const TYPE_ICON: Record<string, React.ReactNode> = {
  text: <TextSnippetRounded sx={{ fontSize: 16 }} />,
  chart: <BarChartRounded sx={{ fontSize: 16 }} />,
  table: <TableChartRounded sx={{ fontSize: 16 }} />,
  finding: <PushPinRounded sx={{ fontSize: 16 }} />,
};

const TYPE_COLOR: Record<string, string> = {
  text: '#6366F1',
  chart: '#059669',
  table: '#D97706',
  finding: '#D97706',
};

const TYPE_LABEL: Record<string, string> = {
  text: 'Text',
  chart: 'Chart',
  table: 'Table',
  finding: 'Finding',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ExportReportModal({
  open,
  onClose,
  threadId,
}: ExportReportModalProps) {
  // Chart artifacts from the store - keyed by tab id
  const workspaceTabs = useCopilotStore((s: any) => s.workspaceTabs) as any[];
  const theme = useTheme();
  const brandName = config.brandName;

  // Curation state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [threadTitle, setThreadTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [items, setItems] = useState<ReportableItem[]>([]);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // ── Curate on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !threadId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setExportError(null);
    setItems([]);
    setSummary('');

    const tid = getTenantIdFromStorage();
    if (!tid) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }
    fetch(buildApiUrl(`${tenantApiPrefix(tid)}/copilot/threads/${threadId}/curate-report`), {
      method: 'POST',
      headers: getAuthHeaders(),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Curation failed (${r.status})`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setThreadTitle(data.thread_title || 'Untitled');
        setSummary(data.summary || '');
        setItems(data.items || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to curate report');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, threadId]);

  // ── Toggle item ─────────────────────────────────────────────────────
  const toggleItem = useCallback((id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, include: !item.include } : item,
      ),
    );
  }, []);

  // ── Generate PDF ────────────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportError(null);

    try {
      const selectedItems = items.filter((i) => i.include);
      if (selectedItems.length === 0) {
        setExportError('Please select at least one item');
        setExporting(false);
        return;
      }

      // Collect raw chart specs from workspaceTabs - backend renders via Playwright + ECharts CDN
      const chartSpecs: Record<string, Record<string, any>> = {};
      const chartItems = selectedItems.filter((i) => i.type === 'chart' && i.artifact_id);
      if (chartItems.length > 0) {
        // Build lookups: eventId → spec (primary), title → spec (fallback)
        const byEventId: Record<string, Record<string, any>> = {};
        const byTitle: Record<string, Record<string, any>> = {};
        for (const tab of workspaceTabs) {
          if (tab.type === 'chart' && tab.spec) {
            if (tab.eventId) byEventId[tab.eventId] = tab.spec;
            if (tab.title) byTitle[tab.title] = tab.spec;
          }
        }
        for (const item of chartItems) {
          const aid = item.artifact_id!;
          const spec = byEventId[aid] || byTitle[item.label] || byTitle[item.label.replace(/^Chart:\s*/, '')];
          if (spec) chartSpecs[aid] = spec;
        }
      }

      // Collect finding_data for selected findings - self-contained in the item
      const findingData: Record<string, any> = {};
      for (const item of selectedItems) {
        if (item.type === 'finding' && item.artifact_id && item.finding_data) {
          findingData[item.artifact_id] = item.finding_data;
        }
      }

      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');

      const resp = await fetch(
        buildApiUrl(`${tenantApiPrefix(tid)}/copilot/threads/${threadId}/export-pdf`),
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            summary,
            selected_items: selectedItems,
            chart_specs: chartSpecs,
            finding_data: findingData,
          }),
        },
      );

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || `Export failed (${resp.status})`);
      }

      // Download the PDF
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Extract filename from Content-Disposition or use default
      const cd = resp.headers.get('Content-Disposition') || '';
      const fnMatch = cd.match(/filename="?(.+?)"?$/);
      a.download = fnMatch ? fnMatch[1] : `${threadTitle || 'report'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      onClose();
    } catch (e: any) {
      setExportError(e.message || 'Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  }, [items, summary, threadId, threadTitle, workspaceTabs, onClose]);

  // ── Counts ──────────────────────────────────────────────────────────
  const selectedCount = items.filter((i) => i.include).length;
  const totalCount = items.length;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      icon={<PictureAsPdfRounded sx={{ color: 'error.main' }} />}
      title="Export report"
      busy={exporting}
      dividers
      primaryAction={{
        label: 'Generate PDF',
        onClick: handleExport,
        icon: <PictureAsPdfRounded />,
        disabled: loading || selectedCount === 0,
      }}
      secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: onClose }}
    >
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
            <CircularProgress size={36} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <AutoAwesomeRounded sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">
                We are curating report items...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Error */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* Content */}
        {!loading && !error && (
          <Box>
            {/* Summary */}
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Executive Summary
            </Typography>
            <TextField
              multiline
              minRows={2}
              maxRows={5}
              fullWidth
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Executive summary of the analysis..."
              size="small"
              sx={{ mb: 2.5 }}
            />

            {/* Items */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Include in report
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedCount} of {totalCount} selected
              </Typography>
            </Box>

            <Box
              sx={{
                maxHeight: 320,
                overflowY: 'auto',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              {items.map((item, idx) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderBottom: idx < items.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                    bgcolor: item.include ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                  }}
                >
                  <Checkbox
                    checked={item.include}
                    onChange={() => toggleItem(item.id)}
                    size="small"
                    sx={{ p: 0.5, mt: 0.25 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                      <Chip
                        icon={TYPE_ICON[item.type] as React.ReactElement}
                        label={TYPE_LABEL[item.type] || item.type}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          bgcolor: alpha(TYPE_COLOR[item.type] || '#666', 0.1),
                          color: TYPE_COLOR[item.type] || '#666',
                          '& .MuiChip-icon': { color: 'inherit' },
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {stripMarkdown(item.label)}
                    </Typography>
                    {item.content_preview && item.type !== 'chart' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.4,
                        }}
                      >
                        {stripMarkdown(item.content_preview)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}

              {items.length === 0 && (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No exportable items found in this conversation.
                  </Typography>
                </Box>
              )}
            </Box>

            {exportError && (
              <Alert severity="error" sx={{ mt: 2 }}>{exportError}</Alert>
            )}
          </Box>
        )}
    </AppDialog>
  );
}
